import { CSSProperties, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { IDropdownMenuItem, IFormField } from '@vivli/shared/infrastructure/interface';
import { Color, Size } from '@vivli/shared/theme';
import { BaseInputFieldComponent } from './base-input-field.component';
import { FormFieldTypeEnum } from '@vivli/shared/infrastructure/enum';
import mergeRefs from 'react-merge-refs';
import { ISelectOption } from '@vivli/features/search/infrastructure/interface';
import { AdvancedSelectComponent } from '../advanced-select.component';

const readonlyStyle: CSSProperties = {
    cursor: 'default',
    display: 'flex',
    padding: '4px',
    paddingLeft: '15px',
    paddingRight: '60px',
    position: 'relative',
    fontSize: Size.FontSize.Large,
    color: Color.DARK_GRAY,
    //matches DropdownFieldComponent so a readonly row is the same height as an editable one
    minHeight: '23px',
    alignItems: 'center'
};

interface AdvancedSelectInputFieldComponentProps extends IFormField {
    items: IDropdownMenuItem[];
    selectText?: string;
    objectKey?: string;
    valueKey?: string;
    isSearchable?: boolean;
    isClearable?: true;
    menuPosition?: 'fixed' | 'absolute';
    menuPlacement?: 'auto' | 'bottom' | 'top';
    targetStyle?: CSSProperties;
    controlHeight?: number;
    sort?: 'asc' | 'desc';
    widerHint?: boolean;
}

export const AdvancedSelectInputFieldComponent = forwardRef((props: AdvancedSelectInputFieldComponentProps, ref) => {
    const selectRef = useRef<HTMLDivElement>();
    const {
        items,
        value,
        defaultValue,
        onChange,
        onBlur,
        readonly,
        label,
        name,
        dataId,
        objectKey,
        valueKey,
        //deliberately no default: an unset selectText keeps react-select's own prompt rather
        //than the legacy '- Select an Option -'. Only a caller that set it explicitly keeps
        //its wording, which is what the two label-less fields rely on. (DEV-961)
        selectText,
        isSearchable = true,
        isClearable,
        //'fixed' pairs with the body portal in AdvancedSelectComponent so the menu escapes a
        //grid's overflow and still follows the control when the grid scrolls (DEV-961)
        menuPosition = 'fixed',
        menuPlacement = 'auto',
        targetStyle,
        controlHeight,
        sort
    } = props;

    /* One key rule, shared by the item list and the stored value, lifted from
     * DropdownFieldComponent.getItem (dropdown-field.component.tsx:89-98). Writing a second
     * rule is how a selection silently stops resolving: the two drift and the field renders
     * the placeholder over a value that is really there. (DEV-961)
     *
     * When objectKey/valueKey is set the legacy component matched on item.value[key], and a
     * stored scalar was compared against that same item.value[key]. Both therefore reduce to
     * one string, which is what react-select wants for ISelectOption.value. */
    const keyProp = valueKey || objectKey;
    const toKey = (raw: any): string => {
        if (raw === null || raw === undefined) {
            return '';
        }
        if (keyProp && typeof raw === 'object') {
            return String(raw[keyProp]);
        }
        return String(raw);
    };

    /* Keyed off items so a list whose CONTENTS change without changing length still rebuilds.
     * AdvancedSelectFieldComponent guards on length and would keep stale options. (DEV-961) */
    const { options, itemsByKey } = useMemo(() => {
        const byKey = new Map<string, IDropdownMenuItem>();
        const built: ISelectOption[] = (items || []).map((item) => {
            const key = toKey(item.value);
            byKey.set(key, item);
            return { value: key, label: item.title };
        });
        return { options: built, itemsByKey: byKey };
    }, [items, keyProp]);

    /* Prefer the controlled value, then the default, exactly as DropdownFieldComponent does.
     * "Neither supplied" stays undefined rather than collapsing to '': an empty option such as
     * organization-policies.component.tsx:137 ({ value: null, title: '' }) keys as '', so mapping
     * an unset field to '' too would match it and render a blank value where the placeholder
     * belongs. The legacy getItem compared with === and null never equalled undefined. (DEV-961) */
    const propKey = value !== undefined ? toKey(value) : defaultValue !== undefined ? toKey(defaultValue) : undefined;

    /* The selection is held locally as well as derived from props, mirroring
     * DropdownFieldComponent's selectedItem state and its effect at dropdown-field.component.tsx:115-118.
     *
     * This is not belt and braces. BaseControlledFormFieldComponent spreads the caller's props
     * AFTER react-hook-form's field, so a call site passing its own `value` overrides field.value
     * - and several do, some with a value that is only ever seeded and never updated on change
     * (listing-request-study-nonNct.component.tsx:101 is one). Deriving the selection purely from
     * props would freeze those fields on their initial value: clicking an option writes to the
     * form but the control never visibly changes. Re-deriving only when the incoming value really
     * changes is what lets the user's pick show through a stale prop. (DEV-961) */
    const [selectedKey, setSelectedKey] = useState<string>(undefined);

    /* Keyed on propKey ALONE. Do not add options here: several call sites build their items
     * array inline in the render body (admin-data-request.component.tsx:47 and
     * admin-virtual-machine.component.tsx:94 both do Object.values(...).map(...)), so the array
     * is a new identity on every render. With options in the deps this effect fired on each of
     * those renders and reset the selection to the incoming prop - and since those two pass no
     * value at all, that meant snapping straight back to the placeholder the instant the parent
     * re-rendered from the change handler. Options arriving late are handled anyway, because the
     * lookup below runs on every render. (DEV-961) */
    useEffect(() => {
        setSelectedKey(propKey);
    }, [propKey]);

    const selectedOption = options.find((o) => o.value === (selectedKey ?? propKey));

    const handleOnChange = (selected: ISelectOption[]) => {
        //AdvancedSelectComponent always hands back an array; this field stores a single value
        const item = itemsByKey.get(selected?.[0]?.value);
        //'' rather than undefined on clear, so ?? above cannot fall back to the value just cleared
        setSelectedKey(selected?.[0]?.value ?? '');

        /* valueKey means "store the scalar, not the object" - DropdownFieldComponent's
         * handleOnChange did exactly this, and the stored shape has to stay identical
         * because nothing is being migrated. objectKey alone stores the whole object. */
        const changedValue = valueKey && item?.value ? item.value[valueKey] : item?.value;
        onChange && onChange(changedValue);
    };

    /* A readonly field renders as plain text, not a disabled control. Fall back to the raw
     * value when it matches no item, so an unresolved selection never collapses into an
     * empty div that reads as "the field is missing". (DEV-711, carried over) */
    const getReadonlyText = () => {
        if (selectedOption?.label) {
            return selectedOption.label;
        }
        if (typeof value === 'string') {
            return value;
        }
        if (typeof defaultValue === 'string') {
            return defaultValue;
        }
        return '';
    };

    const input = (
        <AdvancedSelectComponent
            options={options}
            selectedOptions={selectedOption ? [selectedOption] : null}
            onChange={handleOnChange}
            onBlur={onBlur}
            isSearchable={isSearchable}
            isClearable={isClearable}
            menuPosition={menuPosition}
            menuPlacement={menuPlacement}
            sort={sort}
            controlHeight={controlHeight}
            dataId={dataId || name}
            placeholder={selectText}
            name={name}
            targetStyle={{
                ...targetStyle,
                border: 'none',
                boxShadow: 'none',
                backgroundColor: 'white'
            }}
        />
    );

    return (
        <BaseInputFieldComponent {...props} type={FormFieldTypeEnum.DropDown} inputRef={selectRef} label={label || ''}>
            <div ref={mergeRefs([selectRef, ref])}>{!readonly ? input : <div style={readonlyStyle}>{getReadonlyText()}</div>}</div>
        </BaseInputFieldComponent>
    );
});
